import {type TruncationType} from '../../components/common/TruncateText/TruncateTextTypes';
import {
  type BackendLine,
  type SafePoint,
} from '../../components/PanelRunsLinePlot/componentOutliers/types';
import type * as QueryTypes from '../../util/queryTypes';
import {type RunWithSupportedKeys} from '../runs';
import {type Key, type KeyVal} from '../runTypes';
import {type FancyLegendProps, type LegendData} from './legendTypes';

export type PlotType = 'bar' | 'line' | 'stacked-area' | 'pct-area';

export type Mark =
  | 'dashed'
  | 'dotdash'
  | 'dotdotdash'
  | 'dotted'
  | 'points'
  | 'solid';

export type AggregateCalculation =
  | 'mean'
  | 'minmax'
  | 'none'
  | 'samples'
  | 'stddev'
  | 'stderr';

export type RunSetInfo = Pick<
  QueryTypes.RunSetQuery,
  | 'id'
  | 'grouping'
  | 'entityName'
  | 'projectName'
  | 'runNameTruncationType'
  | 'baselineRunId'
  | 'pinnedRunIds'
>;

export interface Point {
  color?: number | undefined;
  legendData?: LegendData; // info showed realtime in legend on hover-over
  smoothed?: number; // the smoothed value of y
  smoothed0?: number; // smoothed value of y0
  x: number;
  y: number;
  // really y has potential string values, but fixing this is a huge
  // type PR that I don't want to do right now
  // y: number | 'Infinity' | '-Infinity' | 'NaN';
  y0?: number; // for a "point" that defines a span from y to y0
  expressionValues?: Record<string, number>; // metric values needed to compute expressions
}

export interface RunDataSeries {
  color?: string;
  displayName?: string; // This is the friendly name of the run or group
  lineWidth?: number; // thickness of line
  mark?: Mark;
  metricName?: string; // the name of the metric being plotted
  originalColor?: string; // set when color is overridden - should factor out
  originalLineWidth?: number;
  originalMark?: Mark; // set when mark is overridden - should factor out
  originalTitle?: string; // set when title is overwritten by user in config
  runNameTruncationType?: TruncationType; // how to truncate the run name according to the run selector
  title?: string; // legend template, i.e. my-run [[ ${x}: ${y} ]]
  uniqueId?: string; // unique key for every run or group for setting color and overriding attributes
}

/**
 * This is a block of properties which helps simply the disambiguate the business rules for "what lines show when panel config options are enabled". Previously we would mutate properties like "aux" on lines when additional lines were added. This obviously gets hard to reason about so having more fixed properties like "category" makes it trivial to distinguish what kind of line each line is, and makes filtering out whole categories when rules prevent its display.
 *
 * Additionally, future work expects to make use of the `aggregation` type.
 */
export type LineProperties = {
  // we don't use this yet for control flow, but expect to soon
  aggregation:
    | 'none' // used in sampling
    | 'avg'
    | 'median'
    | 'min'
    | 'minmax'
    | 'max'
    | 'stddev'
    | 'stderr'
    | 'samples';
  category: 'default' | 'smoothed' | 'grouped';
  mode: 'full-fidelity' | 'sampled';
  type: 'line' | 'area';
};

export type LineRun = RunWithSupportedKeys & {
  runsetInfo?: {id: string; name?: string | undefined};
  _wandb: KeyVal;
  id: string;
  isBaseline?: boolean;
  isPinned?: boolean;
};

export type LineType = 'area' | 'heatmap' | 'scatter' | 'points' | 'line';

export type Line = {
  // sometimes multiple lines are necessary for a visualization
  // for example showing the light original lines when smoothing
  // is added or the area plots of a grouped line.
  aggType?: AggregateCalculation;
  aux?: boolean; // if aux set to true, don't show in legend overlay i.e. smoothing or minmax
  data: Point[];
  fancyTitle?: FancyLegendProps; // title to use in legend
  groupKeys?: Key[];
  hidden?: boolean; // if true line wont show at all
  historyLines?: BackendLine[];
  domain?: {
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
  };
  meta: LineProperties;
  minmaxLine?: Line; // associated min and max line - should factor out
  name?: string; // name of the associated run or group
  nanPoints?: SafePoint[];
  run?: LineRun;
  smoothed?: boolean;
  smoothedLineFromBackend?: LineWithSource;
  startTimeInMs?: number;
  stddev?: number;
  stddevLine?: Line; // associated stddev line - should factor out
  stderrLine?: Line; // associated stderr line - should factor out
  timestep?: Timestep;
  type?: LineType;
  vars?: {[key: string]: number}; // extra variable in case computing aggregate config or summary
} & RunDataSeries;

export function addSource<T extends Line>(
  line: T,
  source: 'backend' | 'frontend'
): LineWithSource {
  return {
    ...line,
    source,
  };
}

/**
 * As certain behavior moves into the backend (like computing proper x-values with time adjustments) we'll need to distinguish between lines that are computed on the frontend and lines that are computed on the backend in order to reliably toggle processing steps that need to happen for frontend computed data.
 */
export type LineWithSource = Line & {source: 'backend' | 'frontend'};

export type Bar = {
  bins?: Array<{bin: number; count: number}>; // for violinplot
  color?: string;
  key: string; // the label
  mean?: number;
  quartiles?: [number, number, number, number, number]; // for boxplot
  range?: [number, number]; // for error bars
  stddev?: number;
  uniqueId?: string; // unique key for setting color, overriding and highlighting
  value: number; // the value for a regular bar
} & RunDataSeries;

// this is a utility type just
export type TestingLineProperties = Exclude<Line, 'meta'>;

export interface Scalar {
  color?: string;
  key?: FancyLegendProps; // the label
  range?: [number, number];
  stddev?: number;
  stderr?: number;
  uniqueId?: string;
  value: number; // the value for a regular bar
}

export type AxisInfoType = {
  // `undefined` when no finite x value is available (see getXAxisInfo).
  min: number | undefined;
  max: number | undefined;
};

export type Timestep = 'seconds' | 'minutes' | 'hours' | 'days';
